import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  itemsPerPage?: number;
  itemsPerPageOptions?: number[];
  onItemsPerPageChange?: (itemsPerPage: number) => void;
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  itemsPerPageOptions,
  onItemsPerPageChange
}: PaginationProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  // Show only 5 page numbers at a time
  const getVisiblePages = () => {
    if (totalPages <= 5) return pageNumbers;

    if (currentPage <= 3) return pageNumbers.slice(0, 5);
    if (currentPage >= totalPages - 2) return pageNumbers.slice(totalPages - 5);

    return pageNumbers.slice(currentPage - 3, currentPage + 2);
  };

  const visiblePages = getVisiblePages();

  return (
    <div className="flex items-center justify-between w-full">
      {/* Items per page selector */}
      {itemsPerPageOptions && onItemsPerPageChange && (
        <div className="flex items-center space-x-2">
          <label
            htmlFor="items-per-page"
            className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}
          >
            {t('common.itemsPerPage', 'Items per page')}:
          </label>
          <select
            id="items-per-page"
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            className={`block pl-3 pr-8 py-1.5 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
                : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
              }`}
          >
            {itemsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Pagination controls */}
      <nav className="flex items-center justify-center space-x-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`relative inline-flex items-center px-2 py-2 rounded-md text-sm font-medium transition-colors ${currentPage === 1
              ? theme === 'dark'
                ? 'text-gray-600 cursor-not-allowed'
                : 'text-gray-400 cursor-not-allowed'
              : theme === 'dark'
                ? 'text-gray-300 hover:bg-gray-700'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
        >
          {t('common.previous')}
        </button>

        {currentPage > 3 && totalPages > 5 && (
          <>
            <button
              onClick={() => onPageChange(1)}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${theme === 'dark'
                  ? 'text-gray-300 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-50'
                }`}
            >
              1
            </button>
            <span className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'
              }`}>
              ...
            </span>
          </>
        )}

        {visiblePages.map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${currentPage === page
                ? theme === 'dark'
                  ? 'z-10 bg-blue-600 text-white'
                  : 'z-10 bg-blue-600 text-white'
                : theme === 'dark'
                  ? 'text-gray-300 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
          >
            {page}
          </button>
        ))}

        {currentPage < totalPages - 2 && totalPages > 5 && (
          <>
            <span className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'
              }`}>
              ...
            </span>
            <button
              onClick={() => onPageChange(totalPages)}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${theme === 'dark'
                  ? 'text-gray-300 hover:bg-gray-700'
                  : 'text-gray-700 hover:bg-gray-50'
                }`}
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`relative inline-flex items-center px-2 py-2 rounded-md text-sm font-medium transition-colors ${currentPage === totalPages
              ? theme === 'dark'
                ? 'text-gray-600 cursor-not-allowed'
                : 'text-gray-400 cursor-not-allowed'
              : theme === 'dark'
                ? 'text-gray-300 hover:bg-gray-700'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
        >
          {t('common.next')}
        </button>
      </nav>
    </div>
  );
}
